import React, { useState, useRef, useEffect } from 'react';
import { feedbackMap, GetFriendlyOrgNames } from '../utils/utils';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DateRange, RangeKeyDict } from 'react-date-range';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ConsoleLoggingListener } from 'microsoft-cognitiveservices-speech-sdk/distrib/lib/src/common.browser/ConsoleLoggingListener';
import { Cohort, InterviewObject, OrganizationInfo, User } from '@/utils/types';
import Rectangle from './ui/rectangle';
import LinearProgressWithLabel from './ui/progress';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import { formatDateCustom } from '@/utils/dateFormatter';
import Avatar from '@mui/material/Avatar';
import { deepOrange, deepPurple } from '@mui/material/colors';
import TextField from '@mui/material/TextField';
import Nav from './nav';
import { useAppContext } from '../appContext';
import { GetPortalLink, uploadPersonalInfo, uploadUserResume } from '@/api';
import { LogoutButton } from './signin';
import UploadFile from './UploadFile';

/**
 * The UserSettings component renders the user settings page.
 * 
 * It allows the user to view and update their personal information, account information, and resume.
 * The component also provides options to upgrade the plan or manage the subscription.
 */
function UserSettings() {
  const { state, reducers } = useAppContext();

  const [editProfile, seteditProfile] = useState<boolean>(false);
  const [profile, setprofile] = useState<any>({ name: state.profileUser?.name || "" });
  const [loading, setloading] = useState<boolean>(false);
  const [resume, setResume] = useState<{ text: string; file: string }>({
    text: "",
    file: "",
  });

  /**
   * Converts a date string into a Date object.
   * 
   * @param date - The date string to convert.
   * @returns The Date object, or null if the date string is invalid.
   */
  function getDate(date: string): Date | null {
    if (date !== undefined && date !== "" && date !== null) {
      const dateTimeArr = date.split(' ');
      const dateArr = dateTimeArr[0].split('-');
      const timeArr = dateTimeArr[1].split(':');
      return new Date(parseInt(dateArr[0]), parseInt(dateArr[1]) - 1, parseInt(dateArr[2]), parseInt(timeArr[0]), parseInt(timeArr[1]), parseInt(timeArr[2]));
    }
    return null;
  }

  /**
   * Updates the user's profile information.
   * 
   * This function uploads the user's personal information and resume if they have been changed.
   * It then updates the profile state with the new information.
   */
  const updateProfile = async () => {
    let user1;
    let user2;

    if (profile.name !== state.profileUser!.name) {
      setloading(true);
      user1 = await uploadPersonalInfo(profile.name);
    }
    if (resume.text != state.profileUser?.resumename && resume.file != state.profileUser?.resumetext) {
      user2 = await uploadUserResume(resume.text, resume.file);
    }
    if (user1 || user2) {
      reducers.setprofileUser(user2 || user1);
    }
    else {
      seteditProfile(false);
    }
  }

  /**
   * Handles changes to the name input field.
   * 
   * @param event - The input change event.
   */
  const handleNameChange = (event: { target: { value: any; }; }) => {
    setprofile({ ...profile, name: event.target.value }); // Updates state with the input value
  };


  useEffect(() => {
    setloading(false);
    seteditProfile(false);
    setprofile({ name: state.profileUser?.name || "" })
  }, [state.profileUser]);

  useEffect(() => {
    reducers.setoverlayComp(loading);
  }, [loading]);

  return (
    <>
      <Nav />
      {!state.profileUser &&
        <p className='mt-5'>Sign in to get access to your profile</p>
      }
      {state.profileUser &&
        <div className='full-nav'>
          <div className="adminScreen">
            <div className="p-[3%] w-[90%]">

              <div className="flex-col w-[100%] items-start">
                <div className='flex mb-4 items-center text-left w-[100%]'>
                  <Avatar sx={{ minWidth: '56px', minHeight: '56px', bgcolor: deepOrange[500] }}>{(state.profileUser!.name).split(' ').map(word => word[0]).join('')}</Avatar>
                </div>
                <hr />

                <div className='flex justify-between text-left w-[100%]  text-sm'>
                  <div className='flex-col self-start w-[25%]'>
                    <p className="font-bold">Personal Info</p>
                    <p className="text-customBlack">Update your personal details</p>
                  </div>
                  <div className="flex-col gap-4 shadow-sm mb-5 ml-2 p-4 bg-white w-[75%]">
                    <div className='flex-col self-start w-[75%]'>
                      {!editProfile && <p className="text-customBlack">Name</p>}
                      {editProfile && <TextField
                        label="Name"
                        variant="outlined"
                        size="small"
                        defaultValue={state.profileUser.name}
                        onChange={handleNameChange} // Handles changes
                        fullWidth
                        sx={{ flex: 1 }} // Ensures equal width of the boxes
                      />}
                      {!editProfile && <p className="text-black ">{state.profileUser.name}</p>}
                    </div>
                    <div className='flex-col self-start w-[75%]'>
                      <p className="text-customBlack">Email</p>
                      <p className="text-black ">{state.profileUser.email}</p>
                    </div>
                    {!editProfile && <div className='flex-col self-start w-[75%]'>
                      <p className="text-customBlack">Resume</p>
                      <p className="text-black ">{state.profileUser.resumename ? state.profileUser.resumename : 'No resume uploaded'}</p>
                    </div>}
                    {editProfile && <>
                      <p className="text-customBlack">Resume</p> <div className="p-6 border border-[#D5D9EB] rounded-b-[8px]">
                        <UploadFile
                          handleDefaultRemove={() => setResume({ file: "", text: "" })}
                          defaultFileName={state.profileUser.resumename}
                          contentChange={() => setResume({ file: "", text: "" })}
                          handleFunc={(t: string, f: string, e?: string) => { setResume({ file: f, text: t }); }}
                        />
                      </div>
                    </>}

                    <hr />
                    {!editProfile && <div className='w-[unset] border border-customBlack self-end px-3 py-2 cursor-pointer hover:bg-whiteButton-hover font-[300] bg-whiteButton rounded-md shadow-sm' onClick={() => seteditProfile(true)}><p>Edit</p></div>}
                    {editProfile && <div className='flex justify-end gap-2'>
                      <div className='px-3 py-2 cursor-pointer hover:bg-whiteButton-hover font-[300] bg-whiteButton rounded-md shadow-sm' onClick={() => seteditProfile(false)}><p>Cancel</p></div>
                      <div className='px-3 py-2 text-white cursor-pointer hover:bg-grayBlue-hover font-[300] bg-grayBlue rounded-md shadow-sm' onClick={() => updateProfile()}><p>Save Changes</p></div>
                    </div>}


                  </div>

                </div>
                <div className='flex justify-between text-left w-[100%]  text-sm'>
                  <div className='flex-col self-start w-[25%]'>
                    <p className="font-bold">Account Info</p>
                    <p className="text-customBlack">Manage your plan information here</p>
                  </div>
                  <div className="flex-col gap-3 shadow-sm mb-5 ml-2 p-4 bg-white w-[75%]">
                  <div className='flex  justify-between text-left w-[100%]'>
                      <div className="w-[35%]"><p className='mr-5 text-black'>Plan Type</p></div>
                      <p className="text-customBlack">
                          {state.profileUser?.subscription === 'FREE_TIER' ?  "Free" : "Premium"}
                        </p>
                    </div>
                    <div className='flex  justify-between text-left w-[100%]'>
                    <div className="w-[35%]"><p className='mr-5 text-black'>Referral Code</p></div>
                      <p className="text-customBlack">
                          {state.profileUser.referralCode}
                        </p>
                    </div>
                  {state.profileUser?.subscription === 'FREE_TIER' && !state.profileUser?.isLegacyUser && <div className='flex  justify-between text-left w-[100%]'>
                      <div className="w-[35%]"><p className='mr-5 text-black'>Questions Left</p></div>
                      <p className="remainingq text-customBlack">
                          {state.profileUser.questionsLeft} question
                          {state.profileUser.questionsLeft !== 1 ? 's' : ''} left{' '}
                        </p>
                    </div>}
                    {state.profileUser?.organization && state.profileUser?.organization !== "" && <div className='flex justify-between text-left w-[100%]'>
                      <div className="w-[35%]"><p className='mr-5 text-black'>Organization</p></div>
                      <p className='text-customBlack'>{GetFriendlyOrgNames(state.profileUser)}</p>
                    </div>}
                    <hr />
                    {state.profileUser?.subscription === "FREE_TIER" && <div className='w-[unset] border border-customBlack self-end px-3 py-2 cursor-pointer hover:bg-whiteButton-hover font-[300] bg-whiteButton rounded-md shadow-sm' onClick={() => reducers.setshowPaymentModal(true)}><p>Upgrade Plan</p></div>}
                    {state.profileUser?.subscription !== 'FREE_TIER' && (!state.profileUser?.organization || state.profileUser?.organization === "") &&
                      <div className='w-[unset] border border-customBlack self-end px-3 py-2 cursor-pointer hover:bg-whiteButton-hover font-[300] bg-whiteButton rounded-md shadow-sm' onClick={() => GetPortalLink().then((url) => { if (url) { window.open(url, '_blank'); } })}><p>Manage Subscription</p></div>}
                  </div>
                </div>
                <div className="flex items-end self-end justify-end">
                  <LogoutButton />
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    </>
  );
}

export default UserSettings;
